import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
export const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-20px'
}} transition={{
  duration: 0.6,
  delay
}} className={`will-change-transform ${className}`} data-id="element-256">
    {children}
  </motion.div>;
export const Button = ({
  href,
  children,
  variant = 'primary',
  className = ''
}: {
  href: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}) => {
  const baseStyle = 'inline-flex items-center justify-center px-4 md:px-8 py-4 rounded-full font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-xl text-center leading-tight break-words max-w-full';
  const variants = {
    primary: 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 text-navy-950 hover:shadow-[0_0_30px_rgba(250,204,21,0.5)] relative overflow-hidden group border border-yellow-300/50',
    secondary: 'bg-navy-800 text-white border border-navy-600 hover:bg-navy-700 hover:border-gold-500/50'
  };
  return <motion.a href={href} className={`${`${baseStyle} ${variants[variant]} ${className}`} pt-[16px] pb-[16px]`} whileHover={{
    y: -2
  }} animate={variant === 'primary' ? {
    boxShadow: ['0 10px 15px -3px rgba(250, 204, 21, 0.2)', '0 10px 30px -3px rgba(250, 204, 21, 0.6)', '0 10px 15px -3px rgba(250, 204, 21, 0.2)'],
    scale: [1, 1.02, 1]
  } : {}} transition={variant === 'primary' ? {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  } : {}} data-id="element-257">
      {variant === 'primary' && <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:animate-shine" data-id="element-258" />}
      <span className="relative z-10 font-[800]" data-id="element-259">{children}</span>
    </motion.a>;
};
export const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 16,
    seconds: 48
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const format = (num: number) => num.toString().padStart(2, '0');
  const TimeUnit = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center" data-id="element-260">
      <div className="relative bg-navy-900/90 px-3 md:px-5 py-3 md:py-4 rounded-xl border border-red-500/20 shadow-[0_8px_30px_rgba(0,0,0,0.3)] min-w-[64px] md:min-w-[80px]" data-id="element-261">
        {/* Subtle top highlight */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-400/30 to-transparent" data-id="element-262" />

        <div className="relative flex justify-center items-center h-8 md:h-12 overflow-hidden" data-id="element-263">
          <motion.span key={value} initial={{
          y: 15,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -15,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="text-3xl md:text-5xl font-mono font-bold text-red-400 tracking-tighter" data-id="element-264">
            {format(value)}
          </motion.span>
        </div>
      </div>
      <span className="text-[10px] md:text-[11px] text-gray-400 uppercase font-medium tracking-widest mt-3" data-id="element-265">
        {label}
      </span>
    </div>;
  return <div className="flex items-start justify-center space-x-2 md:space-x-4" data-id="element-266">
      <TimeUnit value={timeLeft.hours} label="horas" data-id="element-267" />
      <div className="text-red-500/40 text-2xl md:text-4xl font-light pt-3 md:pt-5 animate-pulse" data-id="element-268">
        :
      </div>
      <TimeUnit value={timeLeft.minutes} label="minutos" data-id="element-269" />
      <div className="text-red-500/40 text-2xl md:text-4xl font-light pt-3 md:pt-5 animate-pulse" data-id="element-270">
        :
      </div>
      <TimeUnit value={timeLeft.seconds} label="segundos" data-id="element-271" />
    </div>;
};